import authService, { TOKEN_TYPES } from "./authService";
import { buildDataEndpointSuffix } from "@/components/Templates/constants";
import { buildApiRequestUrl } from "@/utils/apiUrl";
import { toBackendTrialId } from "@/utils/trialIdMap";
import type {
  AssignResult,
  MarkResult,
  QueueResponse,
  MonitoringPlanResponse,
  MonitoringPlanUpload,
  MonitoringPlanVersion,
  ReviewAssignee,
  ReviewCounts,
  ReviewItemDetail,
  PolicyVersion,
  ReviewSettings,
  ReviewSettingsUpdate,
  SubjectReview,
} from "@/views/MedicalReview/medicalReview.types";

/** Client for the Medical Review endpoints (`/data/trial/<id>/review/…`). */

const base = (trialId: string) =>
  `${import.meta.env.VITE_API_URL}/data/trial/${toBackendTrialId(trialId)}/review`;

export type DatasetRowsPage = {
  count: number;
  results: Record<string, unknown>[];
};

export class MedicalReviewApiError extends Error {
  status: number;
  /** Machine-readable reason, when the API supplies one. */
  code: string;

  constructor(message: string, status: number, code = "") {
    super(message);
    this.name = "MedicalReviewApiError";
    this.status = status;
    this.code = code;
  }
}

export async function request<T>(
  url: string,
  options: { method?: string; body?: unknown } = {},
): Promise<T> {
  const token = authService.getToken(TOKEN_TYPES.ACCESS) ?? "";
  const response = await fetch(url, {
    method: options.method ?? "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    const detail =
      (payload && typeof payload.error === "string" && payload.error) ||
      (payload && typeof payload.detail === "string" && payload.detail) ||
      `Request failed (${response.status})`;
    const code =
      payload && typeof payload.code === "string" ? payload.code : "";
    throw new MedicalReviewApiError(detail, response.status, code);
  }
  return payload as T;
}

async function upload<T>(url: string, form: FormData): Promise<T> {
  const token = authService.getToken(TOKEN_TYPES.ACCESS) ?? "";
  const response = await fetch(url, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    const detail =
      (payload && typeof payload.detail === "string" && payload.detail) ||
      `Upload failed (${response.status})`;
    const code =
      payload && typeof payload.code === "string" ? payload.code : "";
    throw new MedicalReviewApiError(detail, response.status, code);
  }
  return payload as T;
}

const medicalReviewApi = {
  queue: (
    trialId: string,
    params: { domain?: string; subjectId?: string; status?: "reviewed" } = {},
  ): Promise<QueueResponse> => {
    const query = new URLSearchParams();
    if (params.domain) query.set("domain", params.domain);
    if (params.subjectId) query.set("subject_id", params.subjectId);
    if (params.status) query.set("status", params.status);
    const suffix = query.toString() ? `?${query}` : "";
    return request<QueueResponse>(`${base(trialId)}/queue/${suffix}`);
  },

  counts: (trialId: string): Promise<ReviewCounts> =>
    request<ReviewCounts>(`${base(trialId)}/counts/`),

  item: (trialId: string, itemId: number): Promise<ReviewItemDetail> =>
    request<ReviewItemDetail>(`${base(trialId)}/items/${itemId}/`),

  mark: (
    trialId: string,
    body: {
      items: { item_id: number; material_hash: string }[];
      action?: string;
      comment?: string;
    },
  ): Promise<MarkResult> =>
    request<MarkResult>(`${base(trialId)}/marks/`, { method: "POST", body }),

  subject: (trialId: string, subjectId: string): Promise<SubjectReview> =>
    request<SubjectReview>(
      `${base(trialId)}/subjects/${encodeURIComponent(subjectId)}/`,
    ),

  policy: (trialId: string): Promise<ReviewSettings> =>
    request<ReviewSettings>(`${base(trialId)}/policy/`),

  savePolicy: (
    trialId: string,
    body: ReviewSettingsUpdate,
  ): Promise<ReviewSettings & { changes: string[] }> =>
    request(`${base(trialId)}/policy/`, { method: "PUT", body }),

  policyHistory: (trialId: string): Promise<{ versions: PolicyVersion[] }> =>
    request(`${base(trialId)}/policy/history/`),

  /**
   * One page of a review dataset, through the same unified endpoint the
   * advanced table uses. Lets the queue resolve the records behind a filter
   * without waiting for the user to page through them.
   */
  datasetRows: (
    trialId: string,
    datasetType: string,
    body: unknown,
  ): Promise<DatasetRowsPage> =>
    request<DatasetRowsPage>(
      buildApiRequestUrl({
        baseUrl: import.meta.env.VITE_API_URL,
        endpointPrefix: "data/trial",
        id: toBackendTrialId(trialId),
        endpointSuffix: buildDataEndpointSuffix(datasetType),
      }),
      { method: "POST", body },
    ),

  assignees: (trialId: string): Promise<{ people: ReviewAssignee[] }> =>
    request(`${base(trialId)}/assignees/`),

  assign: (
    trialId: string,
    body: {
      items: number[];
      /** Omit to take the records yourself — that is a claim, not an assignment. */
      assignee_id?: string;
      escalate?: boolean;
      unassign?: boolean;
      comment?: string;
    },
  ): Promise<AssignResult> =>
    request<AssignResult>(`${base(trialId)}/assign/`, { method: "POST", body }),

  plan: (trialId: string): Promise<MonitoringPlanResponse> =>
    request<MonitoringPlanResponse>(`${base(trialId)}/plan/`),

  uploadPlan: (
    trialId: string,
    { file, ...fields }: MonitoringPlanUpload,
  ): Promise<MonitoringPlanResponse> => {
    const form = new FormData();
    form.append("file", file);
    for (const [name, value] of Object.entries(fields)) {
      if (value) form.append(name, value);
    }
    return upload<MonitoringPlanResponse>(
      `${base(trialId)}/plan/upload/`,
      form,
    );
  },

  /**
   * Where one version's uploaded document is served from. Bearer-authenticated
   * like every other endpoint here, so it has to be fetched (see
   * `downloadService.downloadFileFromUrl`) rather than put in an `<a href>`.
   */
  planContentUrl: (trialId: string, version: number): string =>
    `${base(trialId)}/plan/${version}/content/`,

  planHistory: (
    trialId: string,
  ): Promise<{ versions: MonitoringPlanVersion[] }> =>
    request(`${base(trialId)}/plan/history/`),

  refresh: (
    trialId: string,
  ): Promise<{
    run_id: number;
    scanned: number;
    new: number;
    material_changes: number;
    administrative_changes: number;
    watermark_at: string | null;
  }> => request(`${base(trialId)}/refresh/`, { method: "POST" }),
};

export default medicalReviewApi;
